import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE } from '../config';
import { useTranslation } from '../translations';
import {
  PanelContainer, Title, Subtitle, PageHeader, HeaderActions,
  StatGrid, StatCard, StatIcon, StatValue, StatLabel, IconBadge,
  PrimaryButton, ToolbarButton, LinkButton, Badge, TextInput, TableWrap,
  FilterField, FilterPillGroup, FilterPill,
} from '../ui/common';
import {
  DownloadIcon, SearchIcon, PlusIcon, WalletIcon, AlertIcon, CheckIcon, SheetIcon,
  CalendarIcon, EditIcon, ArrowRightIcon, SendIcon, RefreshIcon,
} from '../ui/icons';
import SimpleSelect from '../ui/SimpleSelect';
import { Modal, useConfirm } from '../components/ui/Modal';
import { useToast } from '../components/ui/Toast';
import { Banner } from '../components/ui/Banner';
import { EmptyState } from '../components/ui/EmptyState';
import PaginationCompact, { DEFAULT_PAGE_SIZE } from '../components/PaginationCompact';
import { formatMoney, formatDate } from '../utils/format';
import ReservationPicker from '../components/invoices/ReservationPicker';
import { useKsefUwagi, KsefUwagiBanery } from '../components/invoices/KsefUwaga';
import { skrocPrzyczyne } from '../utils/ksefUwaga';
import {
  KSEF_DOC_TYPES, DOC_TYPES, docTypeInfo, paymentStatusInfo, ksefStatusInfo, ksefAkcja, isOverdue,
} from '../utils/invoiceStatus';

const PAYMENT_FILTERS = ['unpaid', 'partial', 'paid'];

// ── Styles ─────────────────────────────────────────────────────────────────
// Uklad jak na Platnosciach (strona-rodzenstwo): tytul z podtytulem, kafelki, filtry, tabela.
// Pola filtrow w jednym rzedzie, pigulki statusu platnosci w drugim — sa nizsze od pol, wiec
// w jednym rzedzie siadalyby na innej linii bazowej.
const FilterBar = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) repeat(3, minmax(0, 1fr));
  gap: var(--sp-3);
  align-items: end;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    & > :first-child { grid-column: 1 / -1; }
  }
  @media (max-width: 600px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const FilterFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-3);
  flex-wrap: wrap;
  margin: var(--sp-4) 0;
`;
const FilterPills = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  flex-wrap: wrap;
`;
const ResultMeta = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  font-size: var(--fs-sm);
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
`;
const SearchInputWrap = styled.div`
  position: relative;
  input { padding-left: calc(var(--sp-8) + 2px); }
  svg { position: absolute; left: var(--sp-3); top: 50%; transform: translateY(-50%); color: var(--text-muted); }
`;

// Tabela w tym samym rysunku co na Platnosciach: naglowek na tle karty, kwoty i daty w cyfrach
// tabelarycznych. Typ dokumentu nie ma juz osobnej kolumny z niebieska pigulka w kazdym wierszu —
// kolor nie niosl znaczenia (patrz utils/invoiceStatus.js), wiec typ stoi cicho pod numerem.
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: var(--fs-base);
  min-width: 880px;

  th, td { padding: var(--sp-3) var(--sp-4); text-align: left; vertical-align: middle; }
  th {
    background: var(--card-bg);
    color: var(--text-muted);
    font-size: var(--fs-xs);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    white-space: nowrap;
    border-bottom: 1px solid var(--input-border);
  }
  tbody tr {
    border-bottom: 1px solid var(--input-border);
    cursor: pointer;
    transition: background var(--dur) var(--ease);
  }
  tbody tr:last-child { border-bottom: none; }
  tbody tr:hover { background: var(--hover-bg); }
  th.num, td.num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  td.date { font-variant-numeric: tabular-nums; white-space: nowrap; color: var(--text-muted); }
  /* Wyrównanie wynika z treści kolumny i obowiązuje RAZEM nagłówek i komórki: tekst do lewej,
     kwoty do prawej, a „kształty" (odznaka, przyciski) na środku. Wyrównane do lewej odznaki
     i przyciski stały pod krótkim nagłówkiem tylko lewą krawędzią — „KSEF" wisiał nad
     początkiem przycisku szerokiego na 140 px i kolumna wyglądała na przesuniętą. */
  th.center, td.center { text-align: center; }
  th.actions, td.actions { width: 1%; text-align: center; }
`;
// Numer jest prawdziwym odnosnikiem: wiersz reaguje na klikniecie myszka, ale z klawiatury da
// sie do niego dojsc wylacznie przez element, ktory przyjmuje fokus.
const NumberLink = styled(Link)`
  color: var(--text);
  font-weight: 700;
  text-decoration: none;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  &:hover { color: var(--primary); }
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; border-radius: 2px; }
`;
const SubLine = styled.div`
  margin-top: 2px;
  font-size: var(--fs-xs);
  font-weight: 500;
  white-space: nowrap;
  color: ${(p) => (p.$tone ? `var(--${p.$tone})` : 'var(--text-muted)')};
`;
const BuyerName = styled.div`
  font-weight: 600;
`;
const DueDate = styled.span`
  color: ${(p) => (p.$overdue ? 'var(--danger)' : 'inherit')};
  font-weight: ${(p) => (p.$overdue ? 600 : 400)};
`;
const KsefCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-2);
  font-size: var(--fs-sm);
`;

// Akcje w wierszu. Były tu goły link („Wyślij do KSeF") i sama ikona pobierania bez obrysu —
// w jedno trudno było trafić, drugiego nie było widać, dopóki kursor na nie nie najechał.
// Teraz to ToolbarButton z ui/common.js (niski, cichy przycisk) o wysokości 36 px, z ikoną
// i podpisem, czyli cel widoczny od razu.
const RowButton = styled(ToolbarButton)`
  min-height: 36px;
  padding: var(--sp-2) var(--sp-3);

  /* Hover przez obrys i kolor, NIE przez tło: wiersz pod kursorem ma już tło --hover-bg, więc
     przycisk z tym samym tłem zlewał się z wierszem i stan najechania znikał. */
  &:hover:not(:disabled) {
    background: var(--card-bg);
    border-color: var(--primary);
    color: var(--primary);
  }
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }
`;

// Wysyłka do KSeF dostaje ton informacyjny, bo to jedyna akcja w wierszu, która wysyła coś
// POZA panel; pod kursorem wypełnia się akcentem, a samolot przesuwa się w stronę wysyłki.
// Prostokąt, nie pigułka: pigułką w tej samej kolumnie jest odznaka statusu KSeF, więc
// przycisk w tym kształcie czytałby się jak kolejny status.
const KsefButton = styled(RowButton)`
  background: var(--info-bg);
  border-color: var(--info-border);
  color: var(--info);

  .send { transition: transform var(--dur) var(--ease); }

  &:hover:not(:disabled) {
    background: var(--primary);
    border-color: transparent;
    color: var(--primary-contrast);
  }
  &:hover:not(:disabled) .send { transform: translate(2px, -2px); }
  &:disabled { opacity: 0.8; cursor: progress; }

  @media (prefers-reduced-motion: reduce) {
    .send { transition: none; }
    &:hover:not(:disabled) .send { transform: none; }
  }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;
const Spinner = styled.span.attrs({ 'aria-hidden': true })`
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 2px solid currentColor;
  border-right-color: transparent;
  animation: ${spin} 0.8s linear infinite;
  @media (prefers-reduced-motion: reduce) { animation-duration: 2.4s; }
`;
const Muted = styled.span`
  color: var(--text-muted);
`;
// Wyjaśnienie, czemu przy fakturze nie ma „Ponów” (duplikat, niepewna dostawa). Zwykłym tekstem,
// nie w dymku: dymka na dotyku nie widać, a od tego zależy, czy ktoś wyśle fakturę drugi raz.
const KsefNote = styled.span`
  max-width: 220px;
  color: var(--text-muted);
  font-size: var(--fs-xs);
  line-height: 1.35;
  text-align: center;
`;
const StateBlock = styled.div`
  padding: var(--sp-8) var(--sp-4);
`;
// Szkielet w miejscu treści, która jeszcze się wczytuje — w rozmiarze docelowej treści, żeby
// po wczytaniu nic nie przeskakiwało (kafelki podsumowania wskakiwały dopiero po odpowiedzi
// i spychały filtry i tabelę w dół).
const pulse = keyframes`
  0%, 100% { opacity: 0.55; }
  50% { opacity: 1; }
`;
const Skeleton = styled.span`
  display: block;
  width: ${(p) => p.$w || '100%'};
  height: ${(p) => p.$h || '1em'};
  border-radius: ${(p) => p.$r || '6px'};
  background: var(--hover-bg);
  animation: ${pulse} 1.4s ease-in-out infinite;
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;
const SkeletonRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
  padding: var(--sp-3);
`;
// Przy kolejnych wczytaniach (filtr, strona, szukanie) tabela ZOSTAJE i tylko przygasa.
// Dawniej każde wczytanie podmieniało ją na napis „Wczytywanie…”, więc strona mrugała.
const BusyTableWrap = styled(TableWrap)`
  transition: opacity 0.15s ease;
  &[aria-busy='true'] { opacity: 0.55; }
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

// Karta wyboru sposobu utworzenia faktury — caly obszar klikalny (plakietka, tytul, opis,
// strzalka), wiec nie jest zwyklym przyciskiem z ui/common.js. Tony plakietek z palety
// ICON_TONES: dusk = czas/terminy (rezerwacja), clay = dokumenty (od zera).
const ChoiceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
`;
const ChoiceButton = styled.button.attrs({ type: 'button' })`
  display: flex;
  align-items: center;
  gap: var(--sp-4);
  width: 100%;
  padding: var(--sp-4) var(--sp-5);
  border: 1px solid var(--input-border);
  border-radius: var(--r-md);
  background: var(--card-bg);
  color: var(--text);
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color var(--dur) var(--ease), background var(--dur) var(--ease);

  & > div:not(:first-child) { flex: 1; min-width: 0; }
  strong { display: block; font-size: var(--fs-md); margin-bottom: 2px; }
  span { color: var(--text-muted); font-size: var(--fs-sm); }
  & > svg { color: var(--text-muted); transition: transform var(--dur) var(--ease), color var(--dur) var(--ease); }

  &:hover { border-color: var(--primary); background: var(--hover-bg); }
  &:hover > svg { color: var(--primary); transform: translateX(2px); }
  &:focus-visible { outline: none; border-color: var(--primary); box-shadow: var(--focus-ring); }

  @media (prefers-reduced-motion: reduce) {
    & > svg { transition: none; }
    &:hover > svg { transform: none; }
  }
`;

export default function Invoices() {
  const navigate = useNavigate();
  const { t, currentLanguage } = useTranslation();
  const confirm = useConfirm();
  const toast = useToast();

  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  // Czy przyszła już JAKAKOLWIEK odpowiedź. Do tego czasu pokazujemy szkielety; potem przy
  // przeładowaniu zostaje poprzednia treść (przygaszona), zamiast znikać i wracać.
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  // Fraza, którą faktycznie wysłaliśmy do serwera (wpisywanie jest opóźnione o 300 ms).
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Numer ostatniego zapytania: odpowiedź na starsze (np. przy szybkim klikaniu filtrów)
  // przychodzi czasem PO nowszej i nadpisywała tabelę nieaktualnymi wynikami.
  const requestRef = useRef(0);
  const [docType, setDocType] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = DEFAULT_PAGE_SIZE;
  // F.19: filtr „nie trafiły do KSeF” siedzi w adresie, bo prowadzi do niego baner ze strony
  // głównej i link z maila do administratorów (/invoices?ksef=attention).
  const [searchParams, setSearchParams] = useSearchParams();
  const ksefFilter = searchParams.get('ksef') === 'attention';
  const { dane: ksefUwagi, odswiez: odswiezUwagiKsef } = useKsefUwagi();

  const [showNewModal, setShowNewModal] = useState(false);
  const [showReservationPicker, setShowReservationPicker] = useState(false);

  // P4.2: stan KSeF per faktura (id -> {status, ksefNumber, ...} | null) + id w trakcie wysyłki
  const [ksefStatuses, setKsefStatuses] = useState({});
  const [ksefSending, setKsefSending] = useState(null);

  const money = (value) => formatMoney(value, { locale: currentLanguage });
  const hasFilters = !!(search.trim() || docType || paymentStatus || dateFrom || dateTo || ksefFilter);

  const setKsefFilter = (wlaczony) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (wlaczony) next.set('ksef', 'attention'); else next.delete('ksef');
      return next;
    }, { replace: true });
    setPage(1);
  };

  const load = async () => {
    const req = ++requestRef.current;
    const isCurrent = () => req === requestRef.current;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (docType) params.set('doc_type', docType);
      if (paymentStatus) params.set('payment_status', paymentStatus);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      if (debouncedSearch) params.set('q', debouncedSearch);
      if (ksefFilter) params.set('ksef', 'attention');
      params.set('limit', pageSize);
      params.set('offset', (page - 1) * pageSize);

      const res = await fetch(`${API_BASE}/api/invoices?${params}`, { credentials: 'include' });
      if (!res.ok) throw new Error(t('invoices.loadError'));
      const data = await res.json();
      if (!isCurrent()) return;
      setInvoices(data.invoices || []);
      setTotal(data.total || 0);
      // Addytywne pole (lib/invoiceSummary.js) — liczone dla całej organizacji, bez filtrów.
      setSummary(data.summary || null);
      loadKsefStatuses(data.invoices || []);
    } catch (e) {
      if (!isCurrent()) return;
      setError(e.message || t('common.error'));
      setInvoices([]);
    } finally {
      if (isCurrent()) {
        setLoading(false);
        setHasLoaded(true);
      }
    }
  };

  // JEDNO miejsce, które wczytuje listę. Wcześniej były dwa: to i efekt wyszukiwarki, który
  // odpalał się także przy wejściu na stronę (z pustą frazą) i 300 ms po pierwszym wczytaniu
  // ładował faktury DRUGI raz — tabela na moment znikała, co wyglądało jak samoczynne
  // odświeżenie strony.
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [docType, paymentStatus, dateFrom, dateTo, page, debouncedSearch, ksefFilter]);

  // Wyszukiwarka tylko przekazuje frazę dalej (z opóźnieniem) i nic nie robi, dopóki fraza
  // się nie zmieniła — dlatego nie wczytuje niczego przy wejściu na stronę.
  useEffect(() => {
    if (search === debouncedSearch) return undefined;
    const timer = setTimeout(() => { setPage(1); setDebouncedSearch(search); }, 300);
    return () => clearTimeout(timer);
  }, [search, debouncedSearch]);

  const clearFilters = () => {
    setSearch('');
    setDocType('');
    setPaymentStatus('');
    setDateFrom('');
    setDateTo('');
    if (ksefFilter) setKsefFilter(false);
    setPage(1);
  };

  const handleNewBlank = () => {
    setShowNewModal(false);
    navigate('/invoices/new');
  };

  const handleNewFromReservation = () => {
    setShowNewModal(false);
    setShowReservationPicker(true);
  };

  const handlePickReservation = (id) => {
    setShowReservationPicker(false);
    navigate(`/invoices/new?reservation=${id}`);
  };

  // P4.2: stan KSeF dla widocznych faktur VAT/KOR (list endpoint zostaje nietknięty —
  // kontrakt bez zmian; strona ma max DEFAULT_PAGE_SIZE wierszy, zwykle kilka VAT/KOR).
  const loadKsefStatuses = async (list) => {
    const targets = list.filter(i => KSEF_DOC_TYPES.includes(i.doc_type));
    if (targets.length === 0) return;
    const entries = await Promise.all(targets.map(async (inv) => {
      try {
        const r = await fetch(`${API_BASE}/api/invoices/${inv.id}/ksef-status`, { credentials: 'include' });
        if (!r.ok) return [inv.id, null];
        const d = await r.json();
        // Bieżące środowisko firmy przy wierszu: wiersz z innego środowiska nie blokuje wysyłki.
        return [inv.id, d.ksef ? { ...d.ksef, currentEnvironment: d.currentEnvironment || null } : null];
      } catch (_) { return [inv.id, null]; }
    }));
    // Merge (nie podmiana) — wyścig odpowiedzi przy zmianie strony nie gubi znanych statusów.
    setKsefStatuses(prev => ({ ...prev, ...Object.fromEntries(entries) }));
  };

  const sendToKsef = async (e, inv) => {
    e.stopPropagation();
    const ok = await confirm({
      title: t('invoices.ksef.sendConfirmTitle'),
      message: t('invoices.ksef.sendConfirmMessage', { number: inv.invoice_number }),
      confirmLabel: t('invoices.ksef.send'),
      tone: 'primary',
    });
    if (!ok) return;
    setKsefSending(inv.id);
    try {
      const csrf = localStorage.getItem('csrfToken') || '';
      const res = await fetch(`${API_BASE}/api/invoices/${inv.id}/ksef-send`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrf },
      });
      const data = await res.json().catch(() => ({}));
      if (data.ksef) {
        setKsefStatuses(prev => ({
          ...prev,
          [inv.id]: { ...data.ksef, currentEnvironment: (prev[inv.id] && prev[inv.id].currentEnvironment) || null },
        }));
      }
      if (res.status === 202) {
        toast.warning(t('invoices.ksef.sendPending'));
      } else if (!res.ok && res.status !== 409) {
        toast.error(data.error || t('invoices.ksef.sendError'));
      }
    } catch (_) {
      toast.error(t('invoices.ksef.sendError'));
    } finally {
      setKsefSending(null);
      odswiezUwagiKsef();
    }
  };

  const downloadUpo = async (e, inv) => {
    e.stopPropagation();
    try {
      const res = await fetch(`${API_BASE}/api/invoices/${inv.id}/ksef-upo`, { credentials: 'include' });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `UPO_${inv.invoice_number.replace(/\//g, '-')}.xml`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_) {
      toast.error(t('invoices.ksef.upoError'));
    }
  };

  const downloadPdf = async (e, id, number) => {
    e.stopPropagation();
    try {
      const res = await fetch(`${API_BASE}/api/invoices/${id}/pdf`, { credentials: 'include' });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${number.replace(/\//g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_) {
      toast.error(t('invoices.pdf.downloadError'));
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const renderKsefCell = (inv) => {
    if (!KSEF_DOC_TYPES.includes(inv.doc_type)) return <Muted>—</Muted>;
    // Stan KSeF dociąga się osobno, po liście. Zanim przyjdzie, NIE pokazujemy „Wyślij do KSeF”:
    // przycisk migał przy każdej fakturze VAT i po chwili zamieniał się np. w „Przyjęta”
    // (fałszywa informacja, że faktura nie została wysłana).
    if (!Object.prototype.hasOwnProperty.call(ksefStatuses, inv.id)) {
      return (
        <KsefCell aria-busy="true" data-cy="invoice-ksef-pending">
          <Skeleton $w="104px" $h="30px" $r="999px" />
        </KsefCell>
      );
    }
    const ks = ksefStatuses[inv.id];
    const ksInfo = ks ? ksefStatusInfo(ks.status) : null;
    const { canSend, retry, noteKey } = ksefAkcja(ks, ks && ks.currentEnvironment);
    const sending = ksefSending === inv.id;
    return (
      <KsefCell>
        {ks && (
          <Badge
            data-cy="invoice-ksef-status"
            $tone={ksInfo.tone}
            title={ks.ksefNumber ? t('invoices.ksef.numberTooltip', { number: ks.ksefNumber }) : (ks.errorMessage || '')}
          >
            {ksInfo.labelKey ? t(ksInfo.labelKey) : ks.status}
          </Badge>
        )}
        {noteKey && <KsefNote data-cy="invoice-ksef-note">{t(noteKey)}</KsefNote>}
        {/* F.19: przyczyna odmowy zwykłym tekstem, nie tylko w dymku, którego na dotyku nie widać. */}
        {!noteKey && ks && (ks.status === 'rejected' || ks.status === 'error') && ks.errorMessage && (
          <KsefNote data-cy="invoice-ksef-reason">{skrocPrzyczyne(ks.errorMessage)}</KsefNote>
        )}
        {canSend && (
          <KsefButton
            data-cy="invoice-ksef-send"
            disabled={sending}
            aria-busy={sending || undefined}
            onClick={(e) => sendToKsef(e, inv)}
          >
            {sending ? <Spinner /> : retry ? <RefreshIcon size={16} /> : <SendIcon size={16} className="send" />}
            {sending ? t('invoices.ksef.sending') : (retry ? t('invoices.ksef.retry') : t('invoices.ksef.send'))}
          </KsefButton>
        )}
        {ks && ks.status === 'upo_received' && (
          <RowButton data-cy="invoice-ksef-upo" onClick={(e) => downloadUpo(e, inv)}>
            <DownloadIcon size={16} /> {t('invoices.ksef.downloadUpo')}
          </RowButton>
        )}
      </KsefCell>
    );
  };

  const renderTable = () => (
    <Table>
      <thead>
        <tr>
          <th>{t('invoices.table.number')}</th>
          <th>{t('invoices.fields.buyer')}</th>
          <th>{t('invoices.table.issued')}</th>
          <th>{t('invoices.table.due')}</th>
          <th className="num">{t('invoices.table.grossAmount')}</th>
          <th className="center">{t('invoices.table.payment')}</th>
          <th className="center">{t('invoices.table.ksef')}</th>
          <th className="actions" aria-label={t('common.actions')} />
        </tr>
      </thead>
      <tbody>
        {invoices.map(inv => {
          const dt = docTypeInfo(inv.doc_type);
          const ps = paymentStatusInfo(inv.payment_status);
          const overdue = isOverdue(inv);
          const statusNote = inv.status === 'corrected'
            ? t('invoices.status.corrected')
            : inv.status === 'draft' ? t('invoices.status.draft') : null;
          const partlyPaid = inv.payment_status === 'partial' && Number(inv.amount_paid) > 0;
          return (
            <tr key={inv.id} data-cy="invoice-row" data-invoice-id={inv.id} onClick={() => navigate(`/invoices/${inv.id}`)}>
              <td>
                <NumberLink to={`/invoices/${inv.id}`} onClick={(e) => e.stopPropagation()}>
                  {inv.invoice_number}
                </NumberLink>
                {/* Korekta dostaje ton ostrzezenia jak w odznace — to jedyny typ, ktory zmienia inny dokument. */}
                <SubLine $tone={dt.tone === 'warning' ? 'warning' : undefined}>
                  {dt.labelKey ? t(dt.labelKey) : inv.doc_type}
                  {statusNote ? ` · ${statusNote}` : ''}
                </SubLine>
              </td>
              <td>
                <BuyerName>{inv.buyer_name}</BuyerName>
                {inv.buyer_nip && <SubLine>{t('invoices.nipLabel', { nip: inv.buyer_nip })}</SubLine>}
              </td>
              <td className="date">{formatDate(inv.issue_date, { locale: currentLanguage })}</td>
              <td className="date">
                {inv.payment_due_date ? (
                  <>
                    <DueDate $overdue={overdue}>{formatDate(inv.payment_due_date, { locale: currentLanguage })}</DueDate>
                    {overdue && <SubLine $tone="danger" data-cy="invoice-overdue">{t('invoices.table.overdue')}</SubLine>}
                  </>
                ) : <Muted>—</Muted>}
              </td>
              <td className="num">
                <strong>{money(inv.total_gross)}</strong>
                {partlyPaid && <SubLine>{t('invoices.table.paidPart', { amount: money(inv.amount_paid) })}</SubLine>}
              </td>
              <td className="center">
                {/* Korekta nie jest należnością (ujemna kwota) — czerwona „Nieopłacona" przy niej
                    sugerowała dług, którego nie ma. */}
                {inv.doc_type === 'CORRECTION'
                  ? <Muted>—</Muted>
                  : <Badge data-cy="invoice-payment-status" $tone={ps.tone}>{t(ps.labelKey)}</Badge>}
              </td>
              <td className="center">{renderKsefCell(inv)}</td>
              <td className="actions">
                <RowButton
                  onClick={(e) => downloadPdf(e, inv.id, inv.invoice_number)}
                  aria-label={`${t('invoices.pdf.download')} ${inv.invoice_number}`}
                  title={t('invoices.pdf.download')}
                >
                  <DownloadIcon size={16} /> PDF
                </RowButton>
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );

  return (
    <PanelContainer>
      <PageHeader>
        <div>
          <Title>{t('invoices.title')}</Title>
          <Subtitle>{t('invoices.subtitle')}</Subtitle>
        </div>
        <HeaderActions data-tour="invoices-new">
          <PrimaryButton data-cy="invoices-new" onClick={() => setShowNewModal(true)}>
            <PlusIcon size={18} /> {t('invoices.new')}
          </PrimaryButton>
        </HeaderActions>
      </PageHeader>

      <KsefUwagiBanery dane={ksefUwagi} onPokaz={ksefFilter ? undefined : () => setKsefFilter(true)} />

      {!hasLoaded && (
        <StatGrid aria-hidden="true" data-cy="invoices-summary-loading">
          {[0, 1, 2].map((i) => (
            <StatCard key={i}>
              <Skeleton $w="40px" $h="40px" $r="12px" />
              <div style={{ flex: 1 }}>
                <Skeleton $w="55%" $h="1.68rem" />
                <Skeleton $w="80%" $h="1.1rem" style={{ marginTop: 4 }} />
              </div>
            </StatCard>
          ))}
        </StatGrid>
      )}
      {hasLoaded && summary && (
        <StatGrid data-cy="invoices-summary">
          <StatCard>
            <StatIcon $tone="gold"><WalletIcon size={20} /></StatIcon>
            <div>
              <StatValue>{money(summary.outstanding_gross)}</StatValue>
              <StatLabel>{t('invoices.stats.outstanding', { count: summary.outstanding_count })}</StatLabel>
            </div>
          </StatCard>
          <StatCard>
            {summary.overdue_count > 0
              ? <StatIcon $tone="danger"><AlertIcon size={20} /></StatIcon>
              : <StatIcon $tone="success"><CheckIcon size={20} /></StatIcon>}
            <div>
              <StatValue>{summary.overdue_count}</StatValue>
              <StatLabel>
                {summary.overdue_count > 0
                  ? t('invoices.stats.overdue', { amount: money(summary.overdue_gross) })
                  : t('invoices.stats.overdueNone')}
              </StatLabel>
            </div>
          </StatCard>
          <StatCard>
            <StatIcon $tone="clay"><SheetIcon size={20} /></StatIcon>
            <div>
              <StatValue>{money(summary.month_gross)}</StatValue>
              <StatLabel>{t('invoices.stats.thisMonth')}</StatLabel>
            </div>
          </StatCard>
        </StatGrid>
      )}

      <FilterBar data-tour="invoices-filters">
        <FilterField>
          {t('invoices.filters.search')}
          <SearchInputWrap>
            <SearchIcon size={16} />
            <TextInput
              type="text"
              placeholder={t('invoices.filters.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </SearchInputWrap>
        </FilterField>
        <FilterField as="div">
          {t('invoices.filters.docType')}
          <SimpleSelect
            style={{ width: '100%' }}
            aria-label={t('invoices.filters.docType')}
            value={docType}
            onChange={(val) => { setDocType(val); setPage(1); }}
            options={[
              { value: '', label: t('invoices.filters.allTypes') },
              ...DOC_TYPES.map((dt) => ({ value: dt, label: t(docTypeInfo(dt).labelKey) })),
            ]}
          />
        </FilterField>
        <FilterField>
          {t('invoices.filters.dateFrom')}
          <TextInput type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} />
        </FilterField>
        <FilterField>
          {t('invoices.filters.dateTo')}
          <TextInput type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} />
        </FilterField>
      </FilterBar>

      <FilterFooter>
        <FilterPills>
          <FilterPillGroup role="group" aria-label={t('invoices.filters.paymentStatus')}>
            <FilterPill $active={!paymentStatus} onClick={() => { setPaymentStatus(''); setPage(1); }}>
              {t('common.all')}
            </FilterPill>
            {PAYMENT_FILTERS.map((ps) => (
              <FilterPill
                key={ps}
                data-cy={`invoices-filter-${ps}`}
                $active={paymentStatus === ps}
                $tone={paymentStatusInfo(ps).tone}
                onClick={() => { setPaymentStatus(paymentStatus === ps ? '' : ps); setPage(1); }}
              >
                {t(`invoices.paymentFilter.${ps}`)}
              </FilterPill>
            ))}
          </FilterPillGroup>
          {(ksefFilter || (ksefUwagi && ksefUwagi.aktywny && ksefUwagi.liczba > 0)) && (
            <FilterPill
              data-cy="invoices-filter-ksef"
              $active={ksefFilter}
              $tone="danger"
              onClick={() => setKsefFilter(!ksefFilter)}
            >
              {t('invoices.filters.ksefAttention')}
            </FilterPill>
          )}
        </FilterPills>
        <ResultMeta>
          {hasLoaded && !error && <span>{t('invoices.resultCount', { count: total })}</span>}
          {hasFilters && <LinkButton onClick={clearFilters}>{t('common.clearFilters')}</LinkButton>}
        </ResultMeta>
      </FilterFooter>

      <BusyTableWrap aria-busy={loading && hasLoaded ? 'true' : undefined}>
        {!hasLoaded ? (
          <SkeletonRows role="status" aria-label={t('invoices.loading')} data-cy="invoices-loading">
            {Array.from({ length: 6 }, (_, i) => <Skeleton key={i} $h="44px" />)}
          </SkeletonRows>
        ) : error ? (
          <StateBlock>
            <Banner tone="danger" action={{ label: t('common.retry'), onClick: load }}>
              {error}
            </Banner>
          </StateBlock>
        ) : invoices.length === 0 ? (
          hasFilters ? (
            <EmptyState
              data-cy="invoices-empty"
              icon={<SearchIcon size={40} />}
              title={t('invoices.emptyFiltered.title')}
              description={t('invoices.emptyFiltered.description')}
              action={{ label: t('common.clearFilters'), onClick: clearFilters }}
            />
          ) : (
            <EmptyState
              data-cy="invoices-empty"
              icon={<SheetIcon size={40} />}
              title={t('invoices.empty.title')}
              description={t('invoices.empty.description')}
              action={{ label: t('invoices.new'), onClick: () => setShowNewModal(true) }}
            />
          )
        ) : renderTable()}
      </BusyTableWrap>

      <PaginationCompact
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        pageSize={pageSize}
        totalItems={total}
      />

      <Modal
        open={showNewModal}
        onClose={() => setShowNewModal(false)}
        title={t('invoices.newModal.title')}
        subtitle={t('invoices.newModal.subtitle')}
        size="sm"
        data-cy="invoice-new-modal"
      >
        <ChoiceList>
          <ChoiceButton data-cy="invoice-new-from-reservation" onClick={handleNewFromReservation}>
            <IconBadge $tone="dusk" $size={44}><CalendarIcon size={20} /></IconBadge>
            <div>
              <strong>{t('invoices.newModal.fromReservationTitle')}</strong>
              <span>{t('invoices.newModal.fromReservationDescription')}</span>
            </div>
            <ArrowRightIcon size={18} />
          </ChoiceButton>
          <ChoiceButton data-cy="invoice-new-blank" onClick={handleNewBlank}>
            <IconBadge $tone="clay" $size={44}><EditIcon size={20} /></IconBadge>
            <div>
              <strong>{t('invoices.newModal.blankTitle')}</strong>
              <span>{t('invoices.newModal.blankDescription')}</span>
            </div>
            <ArrowRightIcon size={18} />
          </ChoiceButton>
        </ChoiceList>
      </Modal>

      <ReservationPicker
        open={showReservationPicker}
        onClose={() => setShowReservationPicker(false)}
        onPick={handlePickReservation}
      />
    </PanelContainer>
  );
}
